import {
    ENCRE_ANTHRACITE,
    SEUIL_CONTRASTE_AA,
    canauxSRGB,
    encreSurMarque,
    ratioDeContraste,
} from '@/lib/contrasteMarque'
import type { EncreDeMarque } from '@/lib/contrasteMarque'
import { rvbVersTsl, tslVersHex } from '@/lib/espaceColorimetrique'

/**
 * La DÉCISION d'habillage d'une couleur de marque — séparée de la MESURE
 * (`contrasteMarque.ts`) et de la CONVERSION (`espaceColorimetrique.ts`).
 *
 * Trois fichiers, trois responsabilités : mesurer un contraste, déplacer une
 * couleur, et choisir. Le défaut d'origine de ce dépôt venait précisément de
 * leur confusion — un seuil de DÉCISION posé sur une grandeur de CONVERSION.
 */

const ENCRE_BLANCHE = '#FFFFFF' as const
/**
 * 🔴 **LE NUDGE BORNÉ — comment garder le BLANC sans détruire une charte.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LE PROBLÈME, ET POURQUOI LES DEUX PREMIÈRES RÉPONSES ÉTAIENT FAUSSES
 * ═══════════════════════════════════════════════════════════════════════════
 * Le PO veut du BLANC sur sa couleur de marque, et il veut UNE seule encre sur
 * toute la page — « on ne peut pas avoir le bouton avec le texte noir dedans si
 * on a le texte blanc sur la gauche ». Les deux exigences sont justes, et deux
 * réponses successives ont échoué :
 *
 * · **deux seuils** (3:1 sur l'aplat, 4,5:1 sur le bouton) → deux encres sur
 *   une même couleur, côte à côte. C'est ce qu'il a refusé, à raison ;
 * · **assombrir toute surface** jusqu'à ce que le blanc passe → marche sur un
 *   magenta, transforme une charte JAUNE en brun. Une règle qui détruit
 *   certaines chartes n'est pas une règle.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LA FRONTIÈRE EST RÉELLE, ET ELLE SE MESURE
 * ═══════════════════════════════════════════════════════════════════════════
 * Combien de points de luminosité faut-il descendre pour que le blanc tienne
 * 4,5:1 ? Relevé sur douze chartes plausibles :
 *
 *     violet, teal        0 pt            magenta du PO      13 pts
 *     rose, rouge      2-3 pts            orange, cyan       12-13 pts
 *     bleu               6 pts            vert                8 pts
 *     ───────────────────────────────────────────────────────────────
 *     lime              20 pts            jaune vif          23 pts
 *     jaune pâle        58 pts
 *
 * Les couleurs CHROMATIQUES demandent au plus 13 points — on les reconnaît
 * encore. Les JAUNES en demandent 20 à 58 et deviennent des bruns. Le seuil de
 * 15 points ne coupe pas au milieu d'un continuum : il sépare deux familles.
 *
 * Le sens de la borne, en une phrase : **si la marque est presque assez sombre
 * pour le blanc, on la pousse ; si elle est fondamentalement claire, le blanc
 * n'était pas la bonne encre.**
 *
 * ⚠️ Teinte et saturation sont CONSERVÉES — seule la luminosité descend. C'est
 * ce qui fait qu'on reconnaît encore la marque ; désaturer rendrait un gris
 * teinté où l'organisme ne se retrouverait pas.
 */
export const NUDGE_MAX_EN_POINTS = 15

/**
 * `{ encre, surfaceInteractive }` — **la décision de la page, prise UNE fois.**
 *
 * `encre` vaut pour TOUT le texte posé sur la marque, aplat compris : c'est la
 * cohérence que le PO exige. `surfaceInteractive` est la couleur des surfaces
 * qui portent du PETIT texte (boutons, badges) — identique à la marque quand
 * aucun nudge n'est nécessaire, légèrement assombrie sinon.
 *
 * ⚠️ **L'aplat de la page d'accès prend AUSSI `surfaceInteractive`**
 * (`PortalAuthLayout`) : il porte un sous-titre et des repères — du petit texte,
 * 4,5:1 exigés — et une page à une seule couleur vaut mieux que deux. Il n'a
 * gardé la couleur brute que tant qu'il ne montrait qu'un titre ; le jour où il
 * ne porterait plus que du grand texte, la brute redeviendrait le bon choix.
 */
export function decisionDeMarque(hexFond: string | null | undefined): {
    encre: EncreDeMarque
    surfaceInteractive: string | null
} {
    if (typeof hexFond !== 'string') return { encre: ENCRE_ANTHRACITE, surfaceInteractive: null }
    const canaux = canauxSRGB(hexFond)
    if (canaux === null) return { encre: ENCRE_ANTHRACITE, surfaceInteractive: null }

    // Le blanc tient déjà partout : rien à pousser.
    if (ratioDeContraste(hexFond, ENCRE_BLANCHE) >= SEUIL_CONTRASTE_AA) {
        return { encre: ENCRE_BLANCHE, surfaceInteractive: null }
    }

    const [teinte, luminosite, saturation] = rvbVersTsl(canaux)
    for (let points = 1; points <= NUDGE_MAX_EN_POINTS; points += 1) {
        const candidat = tslVersHex(teinte, Math.max(luminosite - points / 100, 0), saturation)
        if (ratioDeContraste(candidat, ENCRE_BLANCHE) >= SEUIL_CONTRASTE_AA) {
            return { encre: ENCRE_BLANCHE, surfaceInteractive: candidat }
        }
    }
    // Hors de portée : la marque est fondamentalement claire. Encre sombre
    // partout, surface intacte — c'est le cas d'un jaune, et c'est le bon.
    return { encre: encreSurMarque(hexFond), surfaceInteractive: null }
}
